import React from "react";
import { ChevronDown, Loader } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "./ui/tooltip";
import { cn } from "../lib/utils";
import { fetchChains } from "../lib/api";
import { chainLabel } from "../lib/chainValidation";

/**
 * Chain selector dropdown.
 * Renders supported chains as clickable, unsupported as disabled with tooltip.
 */
export function ChainSelector({ value, onChange, disabled }) {
  const [open, setOpen] = React.useState(false);
  const [chains, setChains] = React.useState(null); // { supported, known }
  const [loading, setLoading] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchChains()
      .then((d) => { if (!cancelled) setChains(d); })
      .catch(() => {
        if (!cancelled)
          setChains({ supported: ["solana"], known: ["solana"] });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const supported = chains?.supported || [];
  const known = chains?.known || [];
  const all = known.length ? known : supported;

  return (
    <div className="relative" ref={ref} data-testid="chain-selector">
      <Button
        type="button"
        variant="secondary"
        size="md"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || loading}
        data-testid="chain-selector-trigger"
        className="min-w-[120px] justify-between gap-1.5"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {loading ? (
          <Loader size={13} className="animate-spin" />
        ) : (
          <ChainDot chain={value} />
        )}
        <span className="flex-1 text-left truncate">{chainLabel(value)}</span>
        <ChevronDown size={13} className={cn("transition-transform", open && "rotate-180")} />
      </Button>

      {open && (
        <ul
          role="listbox"
          data-testid="chain-selector-menu"
          className="absolute z-50 top-[calc(100%+4px)] left-0 min-w-full w-48 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] shadow-lg py-1"
        >
          {all.map((chain) => {
            const isSupported = supported.includes(chain);
            const isActive = chain === value;
            return isSupported ? (
              <li key={chain} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  data-testid={`chain-option-${chain}`}
                  onClick={() => { onChange(chain); setOpen(false); }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[hsl(var(--bg-elev-1))] transition-colors",
                    isActive && "text-[hsl(var(--primary))] font-medium"
                  )}
                >
                  <ChainDot chain={chain} />
                  {chainLabel(chain)}
                  {isActive && <span className="ml-auto text-[10px] text-[hsl(var(--muted))]">✓</span>}
                </button>
              </li>
            ) : (
              <li key={chain} role="option" aria-selected={false} aria-disabled="true">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      data-testid={`chain-option-${chain}-disabled`}
                      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 opacity-40 cursor-not-allowed"
                    >
                      <ChainDot chain={chain} />
                      {chainLabel(chain)}
                      <Badge variant="muted" className="ml-auto text-[9px] px-1 py-0">soon</Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {chainLabel(chain)} support coming soon
                  </TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ChainDot({ chain }) {
  const colors = {
    solana: "bg-[#9945FF]",
    robinhood: "bg-[#00C805]",
    eth: "bg-[#627EEA]",
    base: "bg-[#0052FF]",
    bsc: "bg-[#F3BA2F]",
    tron: "bg-[#FF0013]",
    ton: "bg-[#0088CC]",
    hyperliquid: "bg-[#00E5BE]",
  };
  const color = colors[chain] || "bg-[hsl(var(--muted))]";
  return <span className={cn("inline-block h-2 w-2 rounded-full shrink-0", color)} />;
}
